import React, { useState, useRef } from "react";

import CustomModal from "@components/CustomModal";

import api from "@helpers/api";


import "./styles.scss";

import { dispatchEvent } from "@hooks/useEvent";

import { Text, Button } from "@fragments";
import { DownloadRounded, TuneRounded } from "@mui/icons-material";

import videoHelpers from "@helpers/video";
import EVENTS from "./events";

import Video from "./sections/Video";
import Title from "./sections/Title";
import Description from "./sections/Description";
import LessonOwner from "./sections/LessonOwner";
import SelectCategories from "./sections/SelectCategories";
import Attachments from "./sections/Attachments";
import Availability from "./sections/Availability";
import WaitDaysAfterPayment from "./sections/WaitDaysAfterPayment";
import Launch from "./sections/Launch";

export default function ManageContentModal({ modalRef }) {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    const REF_config = useRef({});
    const REF_callback = useRef();

    modalRef.current = {
        open(preFill, callback = () => { }) {
            REF_config.current = {
                ...preFill,
                availability: preFill.availability || {},
                video: {
                    aspect_ratio: preFill.video?.aspect_ratio || 16 / 9,
                    src: preFill.video?.src || null
                }
            };
            REF_callback.current = callback;
            setOpen(true);
        },
    };

    async function handleSubmit() {
        try {
            validate();

            const payload = getPayload();

            setLoading(true);

            const { data } = await api.post("content", payload);

            if (data.id) {
                await REF_callback.current(data);
                setOpen(false);
            }
        } catch (e) {
            console.log(e);
            window.setSnackbar('Houve um erro ao salvar o conteúdo');
        }

        setLoading(false);

        function getPayload() {
            const {
                id,
                name,
                description,
                banner_base64,
                availability,
                section,
                playlist,
                video,
                attachments,
                creator,
                categories,
                banner,
                timestamps
            } = REF_config.current;

            const data = {
                section: parseInt(section),
                name: name?.trim(),
                description: description?.trim(),
                creator: creator?.id,
                video: {
                    src: typeof video === 'string' ? video : video?.src,
                    aspect_ratio: video?.aspect_ratio
                },
                availability: {
                    wait_days_after_purchase: availability.wait_days_after_purchase,
                    plans: availability.plans ? availability.plans[0] === 'all' ? null : availability.plans : null,
                    launch_at: availability?.launch_at || timestamps?.launch
                },
                banner,
                attachments: attachments || [],
                categories: categories || [],
            };

            if (!!playlist) {
                data.playlist = parseInt(playlist);
            }

            if (!!id) {
                data.id = id;
            }
            if (!!banner_base64 && !banner_base64?.startsWith("http")) {
                data.banner_base64 = banner_base64;
            }

            return data;
        }

        function validate() {
            const { name, description, video } = REF_config.current;

            if (name?.length < 3 || name?.length > 100 || !name)
                throw "Insira um nome válido";
            if (description?.length > 1000)
                throw "Insira uma descrição de no máximo 1.000 caracteres";
            if (!video) throw "Adicione um vídeo e aguarde o processamento";
        }
    }

    async function handleDelete() {
        try {
            const { data } = await api.delete("content", {
                id: REF_config.current.id,
            });
            window.setSnackbar("Conteúdo apagado com sucesso");
            REF_callback.current();
            setOpen(false);
        } catch (e) {
            console.log(e);
        }
    }

    async function handleDownload() {
        try {
            const source = REF_config.current.video.src;

            if (!source) {
                window.setSnackbar('Nenhum vídeo disponível para download');
                return;
            }

            // Check if the source has .m3u8 extension
            if (!source.endsWith('.m3u8')) {
                // If it's not .m3u8, download as is
                downloadFile(source);
                return;
            }

            // Replace .m3u8 with .mp4 or .mov to get the original video
            const baseUrl = source.replace('.m3u8', '');
            const mp4Url = `${baseUrl}.mp4`;
            const movUrl = `${baseUrl}.mov`;

            // Try .mp4 first
            const mp4Exists = await checkFileExists(mp4Url);
            if (mp4Exists) {
                await downloadFile(mp4Url);
                return;
            }

            // Try .mov if .mp4 doesn't exist
            const movExists = await checkFileExists(movUrl);

            if (movExists) {
                await downloadFile(movUrl);
                return;
            }

            // If neither exists, show error
            window.setSnackbar('Vídeo original não encontrado');
        } catch (e) {
            console.log(e);
            window.setSnackbar('Erro ao fazer download do vídeo');
        }
    }

    async function checkFileExists(url) {
        try {
            const response = await fetch(url, { method: 'HEAD' });
            return response.ok;
        } catch (e) {
            return false;
        }
    }

    async function downloadFile(url) {
        try {
            window.setSnackbar('Seu vídeo será baixado em breve, não feche esta tela');

            const response = await fetch(url);
            if (!response.ok) {
                throw new Error('Failed to download file');
            }

            const blob = await response.blob();

            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = url.split('/').pop();
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            window.URL.revokeObjectURL(blobUrl);

            window.setSnackbar('Seu vídeo está sendo baixado');
        } catch (e) {
            console.error('Download error:', e);
            window.setSnackbar('Erro ao fazer download do vídeo');
        }
    }

    return (
        <>
            <CustomModal
                maxWidth={670}
                bottomButtonSumbitText="Salvar conteúdo"
                bottomButtonCancelText="Cancelar"
                loading={loading}
                title="Editar conteúdo"
                deletionTitle="Você realmente deseja apagar este conteúdo permanentemente?"
                handleDelete={handleDelete}
                handleSubmit={handleSubmit}
                topActions={
                    REF_config.current.id && REF_config.current?.video?.src && (
                        <Button
                            onClick={handleDownload}
                            color="overlay"
                            startIcon={<DownloadRounded fontSize="small" />}
                            className="mr-2"
                        >
                            Download
                        </Button>
                    )}
                open={open}
                handleClose={() => setOpen(false)}
                bodyComponent={
                    open ? (
                        <Body configRef={REF_config} />
                    ) : null
                }
            />
        </>
    );
}



function Body({ configRef }) {
    async function handleVideoUploaded(videoInfo) {
        configRef.current.video = { ...configRef.current.video, src: videoInfo.video.path };
        dispatchEvent(EVENTS.VIDEO_URL_CHANGE, videoInfo.video.path);
    }

    function handleRemoveVideo() {
        configRef.current.video = { ...configRef.current.video, src: null };
        dispatchEvent(EVENTS.VIDEO_URL_CHANGE, null);
    }

    function handleBannerLoaded(base64Banner) {
        configRef.current.banner_base64 = base64Banner;
        dispatchEvent(EVENTS.BANNER_CHANGE, base64Banner);
    }

    async function handleGenerateThumb(path) {
        const video = document.createElement("video");
        video.src = path;
        video.onloadedmetadata = () => {
            const ratio = video.videoWidth / video.videoHeight;
            const diffHorizontal = Math.abs(ratio - 1.78);
            const diffVertical = Math.abs(ratio - 0.56);

            const layout = diffVertical < diffHorizontal ? 0.56 : 1.78;
            handlerController.handleChangeAspectRatio(layout);
        };

        if (configRef.current.banner || configRef.current.banner_base64) return;
        const result = await videoHelpers.generateThumbFromLocalVideo(path);
        if (result) handleBannerLoaded(result);
    }

    const handlerController = {
        handleChangeName(value) {
            configRef.current.name = value;
        },
        handleChangeDescription(value) {
            configRef.current.description = value;
        },
        handleChangeOwner(value) {
            configRef.current.creator = { id: value };
        },

        handleChangeCategories(value) {
            configRef.current.categories = value;
        },

        handleChangeAspectRatio(value) {
            configRef.current.video.aspect_ratio = value;
            dispatchEvent(EVENTS.ASPECT_RATIO_CHANGE, value);
        },
        handleChangeAttachments(value) {
            configRef.current.attachments = value;
        },
        handleChangeAvailability(value) {
            configRef.current.availability.plans = value;
            dispatchEvent(EVENTS.AVAILABILITY_CHANGE, value);
        },
        handleChangeWaitDays(value) {
            configRef.current.availability.wait_days_after_purchase = value;
        },
        handleChangeLaunch(value) {
            configRef.current.timestamps.launch = value;
        }
    };

    return (
        <div className="lesson-config-box mt-1 px-4 py-1">
            <Video
                defaultAspectRatio={configRef.current.video?.aspect_ratio}
                defaultBanner={configRef.current.banner_base64 || configRef.current.banner}
                defaultVideoUrl={configRef.current.video?.src}
                onBannerLoaded={handleBannerLoaded}
                onVideoUploaded={handleVideoUploaded}
                onRemoveVideo={handleRemoveVideo}
                onGenerateThumb={handleGenerateThumb}
            />

            <Text.h3
                value="Configurações gerais"
                left={<TuneRounded />}
                className="mb-2 mt-3"
            />
            <Title
                value={configRef.current.name}
                onInput={handlerController.handleChangeName}
            />
            <Description
                value={configRef.current.description}
                onInput={handlerController.handleChangeDescription}
            />

            <LessonOwner
                key={configRef.current.community}
                value={configRef.current.creator?.id}
                onChange={handlerController.handleChangeOwner}
                community={configRef.current.community}
            />

            <SelectCategories
                value={configRef.current.categories}
                onChange={handlerController.handleChangeCategories}
            />

            <Attachments
                value={configRef.current.attachments}
                onChange={handlerController.handleChangeAttachments}
            />

            <Availability
                value={configRef.current.availability}
                onChange={handlerController.handleChangeAvailability}
                community={configRef.current.community}
            />

            <WaitDaysAfterPayment
                value={configRef.current.availability?.wait_days_after_purchase}
                onChange={handlerController.handleChangeWaitDays}
                initialHasPlans={configRef.current.availability?.plans?.length > 0}
            />

            <Launch
                value={configRef.current.timestamps?.launch}
                onChange={handlerController.handleChangeLaunch}
            />
        </div>
    );
}
